import * as _ from "lodash";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {
    IMessageProvider,
    IProgressProvider,
    MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {
    esWB_Report,
    esWB_Report_Metric,
    esWB_Metric_Definition,
    esWB_MasterData_Set,
    esWB_Company,
} from '../PA.Core/PAdbSchema';
import {
    WB_ReportRow,
    WB_ReportMetricRow,
    WB_MetricDefinitionRow,
    WB_MasterDataSetRow,
    WB_CompanyRow,
} from '../PA.Core/PAdbSchemaRows';
import {IwbReport} from '../PA.Core/PAdbSchemaRowsIF';
import {ReportBOValidations} from "./ReportBO.validations";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {DateUtils} from "../PA.Core/Basic/Dates";
import { IJobParam } from "../PA.Job.Core/PA.Job.IF.JobParam";
import { JobParamManager } from "../PA.Job.Core/JobParamManager";
import {WorkspaceReportContext} from "../PA.UIM.Reporting/ReportWorkspaceContext";
import {BehaviorSubject} from "rxjs";

export class ReportBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Report: WB_ReportRow;

    _WorkspaceReport: WorkspaceReportContext
    _hierarchyNodesLoaded: BehaviorSubject<ReportBO>


    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: "New Report"});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);
        this.drWB_Report = null;

        this._WorkspaceReport = new WorkspaceReportContext();
        this._hierarchyNodesLoaded = new BehaviorSubject<ReportBO>(undefined);

    }

    get WB_Report(): WB_ReportRow {
        return this.drWB_Report;
    }

    get WorkspaceReport(): WorkspaceReportContext {
        return this._WorkspaceReport
    }

    //<editor-fold desc = "IBusinessObject operations">

    async LoadNew(options?: object): Promise<IBusinessObject> {
        var self = this;
        this.LoadProgressProvider.start("Creating Report");

        let internalOnComplete = self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {};
                if ("dbContext" in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else self.dbContext = new PAdbContext();

                if (this.dbContext == null) {
                    var sError = "Unable to determine dbContext for new Report.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }


                // Create record using dbContext
                let newReportRow: WB_ReportRow = <WB_ReportRow>await self.dbContext.createRecordWithKey(esWB_Report);
                newReportRow.setDefaults();
                self.drWB_Report = newReportRow;
                //add this to all the BOs
                self.dbContext.add(esWB_Report, newReportRow);

                self.Identity.setAll(
                    self.WB_Report.cif.WB_REPORT_ID,
                    self.WB_Report.cif.REPORT_ID,
                    self.WB_Report.cif.REPORT_NAME
                );
                self.LoadProgressProvider.setName(
                    "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                );
                self.Identity.listenToRecordUpdates(
                    self.WB_Report,
                    "WB_REPORT_ID",
                    "REPORT_ID",
                    "REPORT_NAME"
                );
                await self._AfterBoLoadCompleteInternal()
                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                await self.applyCreateOptions();
                self.Identity.setAll(
                    self.WB_Report.cif.WB_REPORT_ID,
                    self.WB_Report.cif.REPORT_ID,
                    self.WB_Report.cif.REPORT_NAME
                );
                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return internalOnComplete;

    }

    async applyCreateOptions() {
        const self = this;
        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId;
        if (!_.isEmpty(sId))
            self.WB_Report.cif.REPORT_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName;
        if (!_.isEmpty(sName))
            self.WB_Report.cif.REPORT_NAME = sName


        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId

        let wbCompany = self.CreateOptions?.wbCompanyId || self.LoadOptions?.wbCompanyId
        if (_.isNumber(wbMasterDataSet)) {
            let company: WB_CompanyRow = <any>await self.dbContext.find(esWB_Company, wbCompany)
            if (company) {
                self.WB_Report.WB_CompanyRow = company
                self.WB_Report.cif.WB_COMPANY_ID = wbCompany
            }
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_Report.WB_CompanyRow.WB_MasterDataSetRow = mds
                self.WB_Report.WB_CompanyRow.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {
        var self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        if (!this.bIsLoaded)
            this.Identity.setAll(anId, anId, "Loading Report (" + anId + ")");

        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                var sError = "Report already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Report.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = ReportBO.createRecordResolver({
                    dbContext: self.dbContext
                });
                recordResolverLoadPromise = ReportBO.setupRecordResolver(
                    recordResolver,
                    [anId]
                );
            }

            recordResolverLoadPromise
                .then(function () {
                    let aReportRow: any = recordResolver.get(
                        esWB_Report,
                        anId
                    );

                    if (_.isEmpty(aReportRow)) {
                        reject("Report not Found");
                        return;
                    }

                    self.drWB_Report = aReportRow;
                    self.Identity.setAll(
                        self.WB_Report.cif.WB_REPORT_ID,
                        self.WB_Report.cif.REPORT_ID,
                        self.WB_Report.cif.REPORT_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(
                        self.WB_Report,
                        "WB_REPORT_ID",
                        "REPORT_ID",
                        "REPORT_NAME"
                    );
                    return self._AfterBoLoadCompleteInternal()
                }).then(() => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.finish();
                resolve(self);
            })
                .catch(function (err: any) {
                    self._OnLoadComplete = undefined;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });

        return this._OnLoadComplete;
    }


    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    get HierarchyNodesLoaded(): BehaviorSubject<ReportBO> {
        return this._hierarchyNodesLoaded
    }

    private _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this;

        return new Promise<void>((resolve, error) => {
            self.WorkspaceReport.OnWorkspaceContextLoaded.subscribe(() => {
                if (!self.WorkspaceReport.bHierarchiesLoaded) {
                    return
                }
                if (self.WB_Report.cif.WB_CUSTH_ID > 0) {
                    let custHierarchy = _.first(_.filter(self._WorkspaceReport.dsCustomer.Hierarchies, aHierarchy => Number(aHierarchy.HierarchyId) == self.WB_Report.cif.WB_CUSTH_ID))
                    self._WorkspaceReport.dsCustomer.HierarchySelected = custHierarchy

                    self._WorkspaceReport.dsCustomer.HierarchyNodesSelected = _.filter(self._WorkspaceReport.dsCustomer.HierarchyNodes, aNode => _.includes(self.WB_Report.cif.CUSTOMER_NODE_SELECTED.split(","), aNode.NodeId.toString()))
                    self._WorkspaceReport.dsCustomer.OnHierarchyNodesChanged.subscribe(function () {
                        self._WorkspaceReport.dsCustomer.HierarchyNodesSelected = _.filter(self._WorkspaceReport.dsCustomer.HierarchyNodes, aNode => _.includes(self.WB_Report.cif.CUSTOMER_NODE_SELECTED.split(","), aNode.NodeId.toString()))
                        self._hierarchyNodesLoaded.next(self)
                    })
                }

                if (self.WB_Report.cif.WB_PRDH_ID > 0) {
                    let prodHierarchy = _.first(_.filter(self._WorkspaceReport.dsProduct.Hierarchies, aHierarchy => Number(aHierarchy.HierarchyId) == self.WB_Report.cif.WB_PRDH_ID))
                    self._WorkspaceReport.dsProduct.HierarchySelected = prodHierarchy
                    self._WorkspaceReport.dsProduct.HierarchyNodesSelected = _.filter(self._WorkspaceReport.dsProduct.HierarchyNodes, aNode => _.includes(self.WB_Report.cif.PRODUCT_NODE_SELECTED.split(","), aNode.NodeId.toString()))
                    self._WorkspaceReport.dsProduct.OnHierarchyNodesChanged.subscribe(function () {
                        self._WorkspaceReport.dsProduct.HierarchyNodesSelected = _.filter(self._WorkspaceReport.dsProduct.HierarchyNodes, aNode => _.includes(self.WB_Report.cif.PRODUCT_NODE_SELECTED.split(","), aNode.NodeId.toString()))
                        self._hierarchyNodesLoaded.next(self)
                    })
                }

                if (self.WB_Report.cif.WB_PERIODH_ID > 0) {
                    let periodHierarchy = _.first(_.filter(self._WorkspaceReport.dsTime.Hierarchies, aHierarchy => Number(aHierarchy.HierarchyId) == self.WB_Report.cif.WB_PERIODH_ID))
                    self._WorkspaceReport.dsTime.HierarchySelected = periodHierarchy
                    self._WorkspaceReport.dsTime.HierarchyNodesSelected = _.filter(self._WorkspaceReport.dsTime.HierarchyNodes, aNode => _.includes(self.WB_Report.cif.PERIOD_NODE_SELECTED.split(","), aNode.NodeId.toString()))
                    self._WorkspaceReport.dsTime.OnHierarchyNodesChanged.subscribe(function () {
                        self._WorkspaceReport.dsTime.HierarchyNodesSelected = _.filter(self._WorkspaceReport.dsTime.HierarchyNodes, aNode => _.includes(self.WB_Report.cif.PERIOD_NODE_SELECTED.split(","), aNode.NodeId.toString()))
                        self._hierarchyNodesLoaded.next(self)
                    })
                }

                self._WorkspaceReport.aggLevelCustomer = self.WB_Report.cif.CUST_OUTPUT_LEVEL
                self._WorkspaceReport.aggLevelProduct = self.WB_Report.cif.PRD_OUTPUT_LEVEL
                self._WorkspaceReport.aggLevelTime = self.WB_Report.cif.PERIOD_OUTPUT_LEVEL
            });

            resolve()
        })
    }

    Validate(options?: any): Promise<IBusinessObject> {
        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            "Validating " +
            self.WB_Report.cif.REPORT_NAME +
            "(" +
            self.WB_Report.cif.REPORT_ID +
            ")"
        );
        return (async () => {

            // Does no validations yet
            self.ValidateOptions = options || {};

            try {
                if (self.dbContext == null) {
                    var sError = "Unable to determine dbContext to Validate Report";
                    self.ValidateProgressProvider.exception(sError);
                    throw sError;
                }
                await ReportBOValidations.ValidateFields(self);

                if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                    self._bIsValidated = false;
                } else {
                    self._bIsValidated = true;
                }
                self.ValidateProgressProvider.finish();
            } catch (err) {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err)
            } finally {
                return self;
            }
        })()
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;

        try {
            const wbReport = <IwbReport>self.drWB_Report;
            this.SaveProgressProvider.start(
                "Saving " + wbReport.REPORT_NAME + "(" + wbReport.REPORT_ID + ")"
            );

            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError = "Unable to determine dbContext to Save Report.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await self.Validate(options);
            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            self.WB_Report.cif.WB_CUSTH_ID = Number(self._WorkspaceReport?.dsCustomer?.HierarchySelected?.HierarchyId)
            self.WB_Report.cif.WB_PRDH_ID = Number(self._WorkspaceReport?.dsProduct?.HierarchySelected?.HierarchyId)
            self.WB_Report.cif.WB_PERIODH_ID = Number(self._WorkspaceReport?.dsTime?.HierarchySelected?.HierarchyId)
            self.WB_Report.cif.CUST_OUTPUT_LEVEL = self._WorkspaceReport.aggLevelCustomer
            self.WB_Report.cif.PRD_OUTPUT_LEVEL = self._WorkspaceReport.aggLevelProduct
            self.WB_Report.cif.PERIOD_OUTPUT_LEVEL = self._WorkspaceReport.aggLevelTime
            self.WB_Report.cif.CUSTOMER_NODE_SELECTED = _.join(_.map(self._WorkspaceReport?.dsCustomer?.HierarchyNodesSelected, aNode => {
                return aNode?.NodeId
            }), ",")
            self.WB_Report.cif.PRODUCT_NODE_SELECTED = _.join(_.map(self._WorkspaceReport?.dsProduct?.HierarchyNodesSelected, aNode => {
                return aNode?.NodeId
            }), ",")
            self.WB_Report.cif.PERIOD_NODE_SELECTED = _.join(_.map(self._WorkspaceReport?.dsTime?.HierarchyNodesSelected, aNode => {
                return aNode?.NodeId
            }), ",")

            const tasks: Promise<any>[] = [];
            tasks.push(saveFunction(esWB_Report, self.drWB_Report, {}));
            tasks.push(saveFunction(esWB_Report_Metric, self.drWB_Report.WB_ReportMetricRows));
            await Promise.all(tasks);

            if (!self.JobParam) {
                if(self.WB_Report.isActive)
                    await self.IJobParam_OnLoadComplete();
            }

            if (self.JobParam) {
                self.JobParam.id_ui = self.WB_Report.cif.REPORT_ID
                self.JobParam.name = self.WB_Report.cif.REPORT_NAME
                await self.JobParam.Save(bBatch);
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self;
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(
        bSaveNow: boolean,
        options?: any
    ): Promise<IBusinessObject> {
        const self = this;
        const wbReport = <IwbReport>self.drWB_Report;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            "Deleting " + wbReport.REPORT_NAME + "(" + wbReport.WB_REPORT_ID + ")"
        );
        try {
            // 1. Validate if we can delete the Meta

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Report.markDeleted();

            _.forEach(self.drWB_Report.WB_ReportMetricRows, (row) => {
                if (row.isActive) {
                    row.markDeleted();
                }
            });

            if (self.JobParam) {
                await self.JobParam.Delete(bSaveNow);
            }

            if (bSaveNow) {
                await self.SaveUsing(false);
            }

            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            this.DeleteProgressProvider.exception(err);
        }
        return self;
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc="IHaveRecordResolver -- static">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const report_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, report_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, error) => {
            recordResolver
                .findAllWithRelationships(esWB_Report, ids)
                .then(result => {
                    resolve();
                })
                .catch(err => {
                    error(err);
                });
        });
    }

    //</editor-fold>

    //<editor-fold desc="Add/ Remove Metric Definition">

    async addReport(aMtrDfnId: number): Promise<IBusinessObject> {
        const self = this;

        return new Promise<IBusinessObject>(async (resolve, reject) => {

            try {
                let aMtrDfnRow: WB_MetricDefinitionRow;
                const pMtrDfnRow = <WB_MetricDefinitionRow>await self.dbContext.find(esWB_Metric_Definition, aMtrDfnId);
                if (!pMtrDfnRow) {
                    return;
                }
                aMtrDfnRow = pMtrDfnRow;
                const aReportMetricRow = <WB_ReportMetricRow>await self.dbContext.createRecordWithKey(esWB_Report_Metric);
                if (!aMtrDfnRow) {
                    resolve(self);
                    return;
                }
                aReportMetricRow.setDefaults();
                aReportMetricRow.cif.WB_MTRDFN_ID = aMtrDfnRow.cif.WB_MTRDFN_ID;
                aReportMetricRow.WB_MetricDefinitionRow = aMtrDfnRow;
                aReportMetricRow.cif.WB_REPORT_ID = self.WB_Report.cif.WB_REPORT_ID;
                await self.dbContext.add(esWB_Report_Metric, aReportMetricRow);
                resolve(self);
            } catch (error) {
                console.error("An Error Occurred During Adding Row", error);
                reject(error);
            }
        });
    }

    removeReport(amtrDfnId: number): Promise<ReportBO> {
        const self = this

        return new Promise<ReportBO>((resolve, error) => {
            _.forEach(self.drWB_Report.WB_ReportMetricRows, (aReportMetricRow: WB_ReportMetricRow) => {
                if (aReportMetricRow.isActive && aReportMetricRow.cif.WB_MTRDFN_ID === amtrDfnId) {
                    aReportMetricRow.markDeleted();
                }
            });
            resolve(self);
        });
    }

    //</editor-fold>

    //<editor-fold desc = "Job Param">
    _jobParam: IJobParam;

    async IJobParam_OnLoadComplete(): Promise<void> {
        if (!_.isEmpty(this._jobParam)) {
            return;
        }
        const wbReport: IwbReport = this.WB_Report.cif;
        // Enable the job param when Enable Extract is checked, and output levels are defined
        const bIsExtractDisabled: boolean = wbReport.EXTRACT_IND !== "1";
        const bIsMissingOutputLevel: boolean = wbReport.CUST_OUTPUT_LEVEL === "" || wbReport.PRD_OUTPUT_LEVEL === "" || wbReport.PERIOD_OUTPUT_LEVEL === "";
        const bDisabled: boolean = bIsExtractDisabled || bIsMissingOutputLevel;

        const jpMgr = new JobParamManager();
        const companyRow: WB_CompanyRow = this.WB_Report.WB_CompanyRow;

        this._jobParam = await jpMgr.findOrCreate({
            dbContext: this.dbContext,
            JobSchedulerModuleId: "ReportExport",

            sourceType: "Report",
            sourceWbId: wbReport.WB_REPORT_ID,

            appExecId: "Report",
            wbTargetId: wbReport.WB_REPORT_ID,

            wbMasterDataSetId: companyRow.WB_MasterDataSetRow.cif.WB_MDSET_ID,
            wbCompanyId: companyRow.cif.WB_COMPANY_ID,
            wbSalesOrgId: 0,

            JobParamId: `${wbReport.REPORT_ID}_P&LReportExport`,
            JobParamName: `${wbReport.REPORT_NAME}_P&LReportExport`,
            JobProcessorType: "CustomerPnLExportProcessor",
            DependentTriggerId: "CustomerPnLExport",
            bDisabled: bDisabled,
        });
    }

    get JobParam(): IJobParam {
        return this._jobParam;
    }

    //</editor-fold>

    async validateOnChange(aItems: any): Promise<IBusinessObject> {
        return new Promise<IBusinessObject>((resolve, reject) => {
            const self = this;
            ReportBOValidations.ValidateOnSelection(this, aItems).then(() => {
                self.SaveProgressProvider.finish();
                resolve(self);
            })
        })
    }
}

PAEnvironment.registerBusinessObjectClass(ReportBO, "ReportBO", "Report")
